(() => {
  const PRODUCT_ANCHOR_ID = 'gusta-collection-products';
  const PAGINATION_LINK_SELECTOR = '.pagination a[data-page][href]';
  const ACTIVE_PAGE_SELECTOR = '.pagination__nav-item.is-active';
  const FILTER_FORM_SELECTOR = '#desktop-filters-form, #mobile-collection-filters-form';
  const CLEAR_FILTER_SELECTOR = '[data-action="clear-filters"]';
  const DYNAMIC_PART_SELECTOR = '.collection__dynamic-part';
  const INIT_FLAG = '__gustaCollectionPaginationAnchorInitialized';
  const SECOND_SCROLL_DELAY = 150;
  const FILTER_WAIT_TIMEOUT = 10000;

  const productAnchor = document.getElementById(PRODUCT_ANCHOR_ID);

  // This helper belongs only to Gusta family collections that render the
  // landing-card product anchor. Do not initialize anywhere else.
  if (!productAnchor || window[INIT_FLAG]) {
    return;
  }

  window[INIT_FLAG] = true;

  let pendingPage = null;
  let pendingFilterSnapshot = null;
  let observerIsActive = false;
  let delayedScrollTimer = null;
  let filterWaitTimer = null;

  const parsePage = (value) => {
    const page = Number.parseInt(value || '', 10);
    return Number.isFinite(page) && page > 0 ? page : null;
  };

  const getPageFromUrl = () => {
    const url = new URL(window.location.href);
    return parsePage(url.searchParams.get('page')) || 1;
  };

  const getActivePage = () => {
    const activeItem = document.querySelector(ACTIVE_PAGE_SELECTOR);

    if (!activeItem) {
      return null;
    }

    return parsePage(activeItem.dataset.page) || parsePage(activeItem.textContent.trim());
  };

  const getCollectionResultsSnapshot = () => {
    const dynamicPart = document.querySelector(DYNAMIC_PART_SELECTOR);

    if (!dynamicPart) {
      return null;
    }

    const productItems = Array.from(
      dynamicPart.querySelectorAll('.product-list--collection .product-item')
    );
    const productUrls = productItems
      .map((item) => item.querySelector('a[href*="/products/"]')?.getAttribute('href') || '')
      .join('|');
    const productsCount = dynamicPart.querySelector('[data-collection-products-count]');
    const hasSettledResults = Boolean(
      dynamicPart.querySelector('.product-list--collection, .empty-state, [data-collection-products-count]')
    );

    return {
      dynamicPart,
      hasSettledResults,
      signature: [
        productUrls,
        productsCount?.textContent.trim() || '',
        getActivePage() || '',
      ].join('::'),
    };
  };

  const decoratePaginationLink = (link) => {
    const page = parsePage(link.dataset.page);

    // Page 1 must remain a normal collection-top destination.
    if (!page || page <= 1) {
      return;
    }

    const url = new URL(link.getAttribute('href'), window.location.href);
    url.hash = PRODUCT_ANCHOR_ID;

    link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
  };

  const decoratePaginationLinks = (root = document) => {
    root.querySelectorAll(PAGINATION_LINK_SELECTOR).forEach(decoratePaginationLink);
  };

  const scrollToProducts = () => {
    const anchor = document.getElementById(PRODUCT_ANCHOR_ID);

    if (!anchor) {
      return;
    }

    const scroll = () => {
      anchor.scrollIntoView({ block: 'start' });
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        scroll();
        window.clearTimeout(delayedScrollTimer);
        delayedScrollTimer = window.setTimeout(scroll, SECOND_SCROLL_DELAY);
      });
    });
  };

  const observer = new MutationObserver(() => {
    if (pendingPage === null && pendingFilterSnapshot === null) {
      return;
    }

    // Warehouse may replace the dynamic collection markup after either
    // pagination or filtering. Re-decorate fresh pagination links first.
    decoratePaginationLinks(document);
    finishPendingPagination();
    finishPendingFilter();
  });

  const startObserver = () => {
    if (observerIsActive) {
      return;
    }

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    observerIsActive = true;
  };

  const stopObserverIfIdle = () => {
    if (
      !observerIsActive ||
      pendingPage !== null ||
      pendingFilterSnapshot !== null
    ) {
      return;
    }

    observer.disconnect();
    observerIsActive = false;
  };

  function finishPendingPagination() {
    if (pendingPage === null || getActivePage() !== pendingPage) {
      return;
    }

    const completedPage = pendingPage;
    pendingPage = null;
    stopObserverIfIdle();

    // Page 1 keeps Warehouse's normal collection-top behavior. Only page 2+
    // is corrected back down to the Gusta product anchor.
    if (completedPage > 1) {
      scrollToProducts();
    }
  }

  function finishPendingFilter() {
    if (pendingFilterSnapshot === null) {
      return;
    }

    const currentSnapshot = getCollectionResultsSnapshot();

    if (!currentSnapshot || !currentSnapshot.hasSettledResults) {
      return;
    }

    const resultsChanged =
      currentSnapshot.dynamicPart !== pendingFilterSnapshot.dynamicPart ||
      currentSnapshot.signature !== pendingFilterSnapshot.signature;

    if (!resultsChanged) {
      return;
    }

    pendingFilterSnapshot = null;
    window.clearTimeout(filterWaitTimer);
    filterWaitTimer = null;
    stopObserverIfIdle();
    scrollToProducts();
  }

  const waitForPage = (page) => {
    pendingPage = page;
    startObserver();
    finishPendingPagination();
  };

  const waitForFilter = () => {
    const snapshot = getCollectionResultsSnapshot();

    if (!snapshot) {
      return;
    }

    // A filter action supersedes any in-flight pagination destination.
    pendingPage = null;
    pendingFilterSnapshot = snapshot;
    window.clearTimeout(filterWaitTimer);
    filterWaitTimer = window.setTimeout(() => {
      pendingFilterSnapshot = null;
      filterWaitTimer = null;
      stopObserverIfIdle();
    }, FILTER_WAIT_TIMEOUT);

    startObserver();
  };

  document.addEventListener(
    'click',
    (event) => {
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      // Removing an active filter or using Clear/Clear all is handled by
      // Warehouse. We only remember the current results so we can restore the
      // shopper to the product anchor after Warehouse finishes its update.
      if (target.closest(CLEAR_FILTER_SELECTOR)) {
        waitForFilter();
        return;
      }

      const link = target.closest(PAGINATION_LINK_SELECTOR);

      if (!link) {
        return;
      }

      const requestedPage = parsePage(link.dataset.page);

      if (!requestedPage) {
        return;
      }

      // Progressive full-page fallback: update the clicked URL before
      // Warehouse's native handler or the browser consumes it. No native
      // pagination behavior is intercepted or cancelled.
      decoratePaginationLink(link);
      waitForPage(requestedPage);
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;

      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) {
        return;
      }

      const filterForm = target.closest(FILTER_FORM_SELECTOR);

      // Warehouse owns sorting and hidden context values. Only actual facet
      // controls should request the post-filter product-anchor correction.
      if (!filterForm || target.type === 'hidden' || target.name === 'sort_by') {
        return;
      }

      waitForFilter();
    },
    true
  );

  // Preserve the fallback on the initial server-rendered pagination links.
  decoratePaginationLinks(document);

  // A direct/reloaded/full-page page 2+ URL should land at the same product
  // anchor after the server-rendered active page is available.
  const initialPage = getPageFromUrl();
  if (initialPage > 1) {
    waitForPage(initialPage);
  }

  // Browser back/forward can invoke Warehouse's history handling without a
  // pagination click. Observe that requested URL page without changing it.
  window.addEventListener('popstate', () => {
    waitForPage(getPageFromUrl());
  });

  // Keep Theme Editor section refreshes safe without affecting storefront
  // pagination/filter ownership or focus behavior.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) {
      decoratePaginationLinks(event.target);
    }
  });
})();
